import { SessionStatus } from './sessionState';

export const SESSION_REVALIDATION_IDLE_MS = 30 * 60 * 1000;
export const SESSION_REVALIDATION_DEDUPE_MS = 5000;

interface SessionRevalidationControllerOptions {
    getSessionStatus: () => SessionStatus;
    onRevalidate: () => void;
    idleMs?: number;
    dedupeMs?: number;
    now?: () => number;
}

export class SessionRevalidationController {
    private readonly getSessionStatus: () => SessionStatus;
    private readonly onRevalidate: () => void;
    private readonly idleMs: number;
    private readonly dedupeMs: number;
    private readonly now: () => number;
    private inactivityStartedAt: number | null = null;
    private lastRevalidationAt: number | null = null;

    constructor({
        getSessionStatus,
        onRevalidate,
        idleMs = SESSION_REVALIDATION_IDLE_MS,
        dedupeMs = SESSION_REVALIDATION_DEDUPE_MS,
        now = Date.now,
    }: SessionRevalidationControllerOptions) {
        this.getSessionStatus = getSessionStatus;
        this.onRevalidate = onRevalidate;
        this.idleMs = idleMs;
        this.dedupeMs = dedupeMs;
        this.now = now;
    }

    markInactive = () => {
        if (this.inactivityStartedAt === null) {
            this.inactivityStartedAt = this.now();
        }
    };

    revalidateAfterInactivity = () => {
        if (
            this.getSessionStatus() !== SessionStatus.AUTHENTICATED ||
            this.inactivityStartedAt === null
        ) {
            this.inactivityStartedAt = null;
            return;
        }

        const now = this.now();
        const inactiveFor = now - this.inactivityStartedAt;
        this.inactivityStartedAt = null;

        if (inactiveFor < this.idleMs) {
            return;
        }

        if (this.lastRevalidationAt !== null && now - this.lastRevalidationAt < this.dedupeMs) {
            return;
        }

        this.lastRevalidationAt = now;
        this.onRevalidate();
    };
}

interface SubscribeToSessionRevalidationOptions extends SessionRevalidationControllerOptions {
    windowTarget?: Window;
    documentTarget?: Document;
}

export const subscribeToSessionRevalidation = ({
    windowTarget = window,
    documentTarget = document,
    ...controllerOptions
}: SubscribeToSessionRevalidationOptions): (() => void) => {
    const controller = new SessionRevalidationController(controllerOptions);
    const handleFocus = () => {
        if (documentTarget.visibilityState === 'visible') {
            controller.revalidateAfterInactivity();
        }
    };
    const handleVisibilityChange = () => {
        if (documentTarget.visibilityState === 'hidden') {
            controller.markInactive();
            return;
        }

        controller.revalidateAfterInactivity();
    };

    windowTarget.addEventListener('blur', controller.markInactive);
    windowTarget.addEventListener('focus', handleFocus);
    documentTarget.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
        windowTarget.removeEventListener('blur', controller.markInactive);
        windowTarget.removeEventListener('focus', handleFocus);
        documentTarget.removeEventListener('visibilitychange', handleVisibilityChange);
    };
};
